import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { ContentSegment } from "@shared/schema";

// Lazy-load pdfjs only when we actually need it; it ships a big WASM-ish
// blob and we shouldn't pull it in for users who never open a preview.
type PdfJsModule = typeof import("pdfjs-dist");
let pdfjsPromise: Promise<PdfJsModule> | null = null;
const loadPdfJs = (): Promise<PdfJsModule> => {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist").then(async (mod) => {
      // pdfjs needs a worker URL. We point at the bundled worker file
      // that's shipped with pdfjs-dist. Vite resolves the ?url import
      // to a hashed asset.
      const workerUrl = (await import("pdfjs-dist/build/pdf.worker.mjs?url")).default;
      mod.GlobalWorkerOptions.workerSrc = workerUrl;
      return mod;
    });
  }
  return pdfjsPromise;
};

// Map filename / mimetype to a coarse preview category. Used to pick the
// right renderer in the dialog.
type PreviewKind = "pdf" | "docx" | "pptx" | "txt" | "unknown";
const inferKind = (name: string, mime?: string): PreviewKind => {
  const ext = (name.toLowerCase().split(".").pop() || "");
  if (mime?.includes("pdf") || ext === "pdf") return "pdf";
  if (mime?.includes("wordprocessingml") || ext === "docx") return "docx";
  if (mime?.includes("presentationml") || ext === "pptx") return "pptx";
  if (mime?.includes("text/plain") || ext === "txt") return "txt";
  return "unknown";
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uploadId: string | undefined;
  fileName: string;
  mimeType?: string;
  // The extracted-text view is built entirely from data we already have
  // client-side, so it works even if the server file is gone.
  extractedText: string;
  segments?: ContentSegment[];
  /** If provided, the Extracted text tab shows an Edit button. When the
   *  user saves, this callback fires with the full new text. Per-segment
   *  anchoring is dropped (segments collapse to a single block) since
   *  free-form edits invalidate page/slide boundaries. */
  onSaveExtractedText?: (newText: string) => void;
}

// Render PDF pages into <canvas> elements stacked vertically.
function PdfVisualPreview({ uploadId }: { uploadId: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        const res = await fetch(`/api/uploads/${uploadId}`);
        if (!res.ok) throw new Error(`fetch failed (${res.status})`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        const pdf = await pdfjs.getDocument({ data: buf }).promise;
        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = "";
        // Render at 2x for retina-quality, then constrain display width
        // to the container via CSS. This keeps text sharp at any zoom.
        const renderScale = 2;
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: renderScale });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = "shadow-xs border bg-white block mb-4";
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          container.appendChild(canvas);
          await page.render({ canvasContext: ctx, viewport, canvas }).promise;
        }
        if (!cancelled) setStatus("ready");
      } catch (err) {
        console.error("[preview] PDF render failed:", err);
        if (!cancelled) {
          setErrorMessage(err instanceof Error ? err.message : "Unknown error");
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uploadId]);

  return (
    <div className="space-y-2">
      {status === "loading" && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
          Rendering PDF…
        </div>
      )}
      {status === "error" && (
        <div className="text-sm text-destructive py-4">
          Couldn't render preview ({errorMessage}). Use the Text tab instead.
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
}

// Fetch and inject the server-rendered HTML (from mammoth) for a DOCX.
// Sanitized client-side because mammoth output is generally safe but
// trusting that without sanitization would be a bad pattern.
function DocxVisualPreview({ uploadId }: { uploadId: string }) {
  const [html, setHtml] = useState<string>("");
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/uploads/${uploadId}/html`);
        if (!res.ok) throw new Error(`fetch failed (${res.status})`);
        const raw = await res.text();
        if (cancelled) return;
        setHtml(DOMPurify.sanitize(raw));
        setStatus("ready");
      } catch (err) {
        console.error("[preview] DOCX render failed:", err);
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uploadId]);

  if (status === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" />
        Rendering document…
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="text-sm text-destructive py-4">
        Couldn't render this Word document. Use the Text tab instead.
      </div>
    );
  }
  return (
    <article
      className="prose prose-sm max-w-none [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// PPTX: no native renderer available client-side. Show each slide as a
// card with a slide-like aspect ratio and the extracted slide text. It's
// not a true visual reproduction but it's much more useful than a wall
// of text, and matches the per-slide structure of the source.
function PptxSlidePreview({ segments }: { segments?: ContentSegment[] }) {
  const slides = (segments ?? []).filter((s) => s.anchor?.kind === "pptx-slide");
  if (slides.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No slide-level content was extracted from this presentation. Use the Text tab.
      </p>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground italic">
        Slide text only — a true visual render of PowerPoint slides isn't available in-browser.
      </p>
      {slides.map((slide, i) => (
        <div
          key={i}
          className="aspect-video border bg-white shadow-xs rounded-md p-6 overflow-hidden flex flex-col"
        >
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
            Slide {slide.anchor?.kind === "pptx-slide" ? slide.anchor.slide : i + 1}
          </div>
          <div className="text-sm whitespace-pre-wrap leading-relaxed flex-1 overflow-auto">
            {slide.text}
          </div>
        </div>
      ))}
    </div>
  );
}

// TXT: simple monospace dump.
function TxtVisualPreview({ extractedText }: { extractedText: string }) {
  return (
    <pre className="text-xs font-mono whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-md p-4">
      {extractedText}
    </pre>
  );
}

// Text-only view: render extracted content per segment with a header.
// Falls back to a single block if no segments exist. If onSave is
// provided, an Edit button switches to a single-textarea edit mode.
function ExtractedTextView({
  extractedText,
  segments,
  onSave,
}: {
  extractedText: string;
  segments?: ContentSegment[];
  onSave?: (text: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(extractedText);

  // Reset the draft when the underlying text changes (e.g. user opened
  // the dialog for a different material) or when leaving edit mode.
  useEffect(() => {
    setDraft(extractedText);
    setEditing(false);
  }, [extractedText]);

  if (editing) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground italic">
          Editing extracted text. Saving will replace what gets sent to the AI for this material.
          Per-page / per-slide / per-heading anchoring is lost on save.
        </p>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="min-h-[400px] font-mono text-xs leading-relaxed resize-none"
          data-testid="textarea-extracted-text-edit"
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setDraft(extractedText);
              setEditing(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSave?.(draft);
              setEditing(false);
            }}
          >
            Save changes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground italic">
          This is the text that will be sent to the AI.{' '}
          {segments && segments.length > 0
            ? `${segments.length} ${segments.length === 1 ? 'section' : 'sections'} extracted.`
            : ''}
        </p>
        {onSave && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setEditing(true)}
            data-testid="button-edit-extracted-text"
          >
            Edit <Pencil className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      {!segments || segments.length === 0 ? (
        <pre className="text-xs font-mono whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-md p-4">
          {extractedText}
        </pre>
      ) : (
        segments.map((seg, i) => {
          let label = `Section ${i + 1}`;
          if (seg.anchor?.kind === "pdf-page") label = `Page ${seg.anchor.page}`;
          else if (seg.anchor?.kind === "pptx-slide") label = `Slide ${seg.anchor.slide}`;
          else if (seg.anchor?.kind === "docx-heading") label = seg.anchor.heading || label;
          return (
            <div key={i} className="space-y-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {label}
              </p>
              <pre className="text-xs font-mono whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-md p-3">
                {seg.text}
              </pre>
            </div>
          );
        })
      )}
    </div>
  );
}

export function MaterialPreviewDialog({
  open,
  onOpenChange,
  uploadId,
  fileName,
  mimeType,
  extractedText,
  segments,
  onSaveExtractedText,
}: Props) {
  const kind = inferKind(fileName, mimeType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="break-all">{fileName}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="visual" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-6 mt-3 self-start">
            <TabsTrigger value="visual">Visual</TabsTrigger>
            <TabsTrigger value="text">Extracted text</TabsTrigger>
          </TabsList>
          <TabsContent value="visual" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            {!uploadId && kind !== "txt" ? (
              <p className="text-sm text-muted-foreground py-4">
                No preview available for this material (it was added in an older session).
                Use the Text tab to see the content.
              </p>
            ) : kind === "pdf" && uploadId ? (
              <PdfVisualPreview uploadId={uploadId} />
            ) : kind === "docx" && uploadId ? (
              <DocxVisualPreview uploadId={uploadId} />
            ) : kind === "pptx" ? (
              <PptxSlidePreview segments={segments} />
            ) : kind === "txt" ? (
              <TxtVisualPreview extractedText={extractedText} />
            ) : (
              <p className="text-sm text-muted-foreground py-4">
                No visual preview is available for this format. Use the Text tab to see the content.
              </p>
            )}
          </TabsContent>
          <TabsContent value="text" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            <ExtractedTextView
              extractedText={extractedText}
              segments={segments}
              onSave={onSaveExtractedText}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
